import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatDate } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ArrowLeft, Mail, Search, ShieldAlert } from "lucide-react";

const STATUS_OPTIONS = ["", "mock_sent", "sent", "error"];
const KIND_OPTIONS = ["", "proposal", "contract", "invoice", "customer_update"];
const LANG_OPTIONS = ["", "en", "es"];

const STATUS_STYLES = {
  mock_sent: "bg-[var(--jc-border)] text-[var(--jc-text)]",
  sent: "bg-[var(--jc-teal)]/15 text-[var(--jc-teal)]",
  error: "bg-[var(--jc-red)]/15 text-[var(--jc-red)]",
  queued: "bg-[var(--jc-blue)]/15 text-[var(--jc-blue)]",
};

const EmailOutbox = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", kind: "", language: "", q: "" });
  const [openDetail, setOpenDetail] = useState(null); // outbox doc
  const [detailLoading, setDetailLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    // Backend uses `status_eq` to avoid shadowing FastAPI's `status` module.
    if (filters.status) params.append("status_eq", filters.status);
    if (filters.kind) params.append("kind", filters.kind);
    if (filters.language) params.append("language", filters.language);
    if (filters.q) params.append("q", filters.q);
    try {
      const { data } = await api.get(`/admin/email-outbox?${params.toString()}`);
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const setFilter = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const counts = useMemo(() => ({
    total: rows.length,
    mock: rows.filter((r) => r.status === "mock_sent").length,
    sent: rows.filter((r) => r.status === "sent").length,
    error: rows.filter((r) => r.status === "error").length,
  }), [rows]);

  const openDetailFor = async (id) => {
    setDetailLoading(true);
    setOpenDetail({ id, loading: true });
    try {
      const { data } = await api.get(`/admin/email-outbox/${id}`);
      setOpenDetail(data);
    } catch {
      setOpenDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <DashboardLayout
      title="Email Outbox"
      subtitle="Read-only ledger of every email JobClose AI has queued, mocked, or sent."
      actions={
        <Link to="/app/admin" className="text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Back to Admin
        </Link>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPI label="Showing" value={counts.total} />
        <KPI label="Mock sent" value={counts.mock} muted />
        <KPI label="Sent (live)" value={counts.sent} accent="teal" />
        <KPI label="Errors" value={counts.error} accent="red" />
      </div>

      <div className="jc-card p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="email-outbox-filters">
        <Field label="Status">
          <select data-testid="filter-status" value={filters.status} onChange={setFilter("status")} className="px-2.5 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm">
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s ? s.replace("_", " ") : "All"}</option>)}
          </select>
        </Field>
        <Field label="Kind">
          <select data-testid="filter-kind" value={filters.kind} onChange={setFilter("kind")} className="px-2.5 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm">
            {KIND_OPTIONS.map((k) => <option key={k} value={k}>{k ? k.replace("_", " ") : "All"}</option>)}
          </select>
        </Field>
        <Field label="Language">
          <select data-testid="filter-language" value={filters.language} onChange={setFilter("language")} className="px-2.5 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm">
            {LANG_OPTIONS.map((l) => <option key={l} value={l}>{l ? l.toUpperCase() : "All"}</option>)}
          </select>
        </Field>
        <Field label="Recipient email">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--jc-text-soft)]" />
            <input
              type="text"
              data-testid="filter-recipient"
              value={filters.q}
              onChange={setFilter("q")}
              placeholder="customer@..."
              className="pl-7 pr-2.5 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm min-w-[240px]"
            />
          </div>
        </Field>
        <button onClick={refresh} data-testid="apply-filters-btn" className="px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary">
          Apply
        </button>
      </div>

      <div className="jc-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="email-outbox-table">
          <thead className="bg-[var(--jc-bg)] text-left text-[var(--jc-text-soft)]">
            <tr>
              <Th>Sent at</Th>
              <Th>Recipient</Th>
              <Th>Kind</Th>
              <Th>Status</Th>
              <Th>Lang</Th>
              <Th>Related id</Th>
              <Th>Subject</Th>
              <Th>{""}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--jc-border)]">
            {loading && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-[var(--jc-text-soft)]">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[var(--jc-text-soft)]">No emails match your filters yet.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={`outbox-row-${r.id}`} className="hover:bg-[var(--jc-bg)]">
                <Td className="whitespace-nowrap">{formatDate(r.created_at)}</Td>
                <Td className="font-mono-jc text-xs">{r.to}</Td>
                <Td className="capitalize">{(r.kind || "").replace("_", " ")}</Td>
                <Td>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider ${STATUS_STYLES[r.status] || "bg-[var(--jc-border)]"}`}>
                    {r.status}
                  </span>
                </Td>
                <Td>
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-[var(--jc-border)] text-[var(--jc-text)]">
                    {(r.language || "en").toUpperCase()}
                  </span>
                </Td>
                <Td className="font-mono-jc text-[11px] text-[var(--jc-text-soft)]">{(r.related_id || "—").slice(0, 8)}</Td>
                <Td className="max-w-[280px] truncate">{r.subject}</Td>
                <Td>
                  <button
                    onClick={() => openDetailFor(r.id)}
                    data-testid={`outbox-view-${r.id}`}
                    className="text-xs font-semibold text-[var(--jc-blue)] hover:underline"
                  >
                    View details
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-[var(--jc-text-soft)] inline-flex items-center gap-1.5">
        <ShieldAlert size={13} /> Read-only · no API keys or secrets are exposed by this page · resend/retry is intentionally NOT enabled during beta.
      </p>

      <Dialog open={!!openDetail} onOpenChange={(o) => { if (!o) setOpenDetail(null); }}>
        <DialogContent data-testid="outbox-detail-dialog" className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={16}/> Email detail</DialogTitle>
            <DialogDescription>Snapshot of the queued/mocked/sent email. Provider response shown if available.</DialogDescription>
          </DialogHeader>
          {detailLoading || !openDetail ? (
            <div className="py-6 text-sm text-[var(--jc-text-soft)]">Loading…</div>
          ) : (
            <div className="space-y-4 text-sm">
              <Pair label="Subject" value={openDetail.subject} mono={false} />
              <Pair label="To" value={openDetail.to} />
              <Pair label="From" value={openDetail.from} />
              <Pair label="Status" value={openDetail.status} />
              <Pair label="Mode" value={openDetail.status === "mock_sent" ? "mock (no real send)" : "live"} />
              <Pair label="Language" value={(openDetail.language || "en").toUpperCase()} />
              <Pair label="Kind" value={openDetail.kind} />
              <Pair label="Related id" value={openDetail.related_id || "—"} />
              <Pair label="Created at" value={formatDate(openDetail.created_at)} />
              {openDetail.provider_id && <Pair label="Provider id" value={openDetail.provider_id} />}
              {openDetail.provider_response && <Pair label="Provider response" value={openDetail.provider_response} />}
              {openDetail.error && (
                <Pair label="Error" value={openDetail.error} accent="red" />
              )}
              <div>
                <div className="overline mb-2">Email HTML preview</div>
                <div
                  data-testid="outbox-detail-html-preview"
                  className="rounded-md border border-[var(--jc-border)] bg-white p-3 max-h-[420px] overflow-auto"
                  // Rendering as HTML so the admin can sanity-check what the customer
                  // would see. Source is JobClose AI's own templates with escaped
                  // user-provided strings — not externally controlled content.
                  dangerouslySetInnerHTML={{ __html: openDetail.html || "<em>(empty)</em>" }}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

const KPI = ({ label, value, accent, muted }) => {
  const tone = accent === "teal" ? "text-[var(--jc-teal)]" :
               accent === "red" ? "text-[var(--jc-red)]" :
               muted ? "text-[var(--jc-text-soft)]" : "text-[var(--jc-text)]";
  return (
    <div className="jc-card p-4">
      <div className="overline">{label}</div>
      <div className={`font-display text-2xl mt-1 ${tone}`}>{value}</div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-wider text-[var(--jc-text-soft)] font-semibold">{label}</span>
    {children}
  </label>
);

const Th = ({ children }) => (
  <th className="px-4 py-2.5 text-[11px] tracking-wider uppercase font-semibold">{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-2.5 align-top ${className}`}>{children}</td>
);

const Pair = ({ label, value, accent, mono = true }) => (
  <div className="grid grid-cols-[140px_1fr] gap-3">
    <div className="overline">{label}</div>
    <div className={`${mono ? "font-mono-jc text-[12px]" : ""} ${accent === "red" ? "text-[var(--jc-red)]" : ""} whitespace-pre-wrap break-all`}>
      {value}
    </div>
  </div>
);

export default EmailOutbox;
